import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import * as monthUtils from '@actual-app/core/shared/months';

import { Boton } from '#components/mobile/ui/Boton';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useFormat } from '#hooks/useFormat';
import { SheetNameProvider } from '#hooks/useSheetName';
import { useSheetValue } from '#hooks/useSheetValue';
import { addNotification } from '#notifications/notificationsSlice';
import { useDispatch } from '#redux';
import { envelopeBudget } from '#spreadsheet/bindings';

import { recortarReparto } from './nomina';
import type { PlanPaga } from './nomina';
import { repartirPaga } from './repartir';
import { usePlanesPagas } from './useNomina';

type BotonRepartirProps = {
  clave: string;
  /** Mes en que se cobró la paga (`AAAA-MM`). */
  mes: string;
  plan: PlanPaga;
  onHecho: () => void;
};

/**
 * «Repartir 1.200 €» de una paga ya cobrada: suma a cada categoría su parte
 * (nunca más de lo que hay por asignar), deja el resto en «Listo para
 * asignar» y marca la paga como repartida.
 */
export function BotonRepartir(props: BotonRepartirProps) {
  return (
    <SheetNameProvider name={monthUtils.sheetForMonth(props.mes)}>
      <BotonRepartirMes {...props} />
    </SheetNameProvider>
  );
}

function BotonRepartirMes({ clave, mes, plan, onHecho }: BotonRepartirProps) {
  const { t } = useTranslation();
  const format = useFormat();
  const dispatch = useDispatch();
  const { guardarPlan } = usePlanesPagas();
  const [repartiendo, setRepartiendo] = useState(false);
  const porAsignar =
    useSheetValue<'envelope-budget', 'to-budget'>(envelopeBudget.toBudget) ?? 0;
  const aCategorias = recortarReparto(
    plan.destinos.filter(d => d.tipo === 'categoria'),
    porAsignar,
  );
  const total = aCategorias.reduce((s, d) => s + d.importe, 0);

  const repartir = async () => {
    setRepartiendo(true);
    try {
      await repartirPaga(mes, aCategorias);
      guardarPlan(clave, { ...plan, estado: 'repartida' });
      if (total > 0) {
        dispatch(
          addNotification({
            notification: {
              type: 'message',
              message: t('Payment shared out: {{amount}}', {
                amount: format(total, 'financial'),
              }),
            },
          }),
        );
      }
      onHecho();
    } finally {
      setRepartiendo(false);
    }
  };

  return (
    <Boton
      data-testid="plan-paga-repartir"
      isDisabled={repartiendo || plan.destinos.length === 0}
      onPress={() => void repartir()}
    >
      {total > 0 ? (
        <PrivacyFilter>
          {t('Share out {{amount}}', { amount: format(total, 'financial') })}
        </PrivacyFilter>
      ) : (
        <Trans>Done</Trans>
      )}
    </Boton>
  );
}
